import { MEDIA_UPLOAD_LIMIT_BYTES } from '@ghp/domain'
import { Button, Field, InfoNote, Input, Modal, SegmentedControl } from '@ghp/ui'
import { Check, FilePlus2, FileText, Info, Video } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'

import s from './AddFileModal.module.css'

/**
 * Ce que l'agent joint au dossier : une photo prise sur place, un document importé de la
 * tablette (PDF, plan) ou une vidéo.
 *
 * ⚠️ **Ce n'est pas la famille de la pièce** (`AttachmentType` : vue, regard, eaux pluviales),
 * qui dit ce que la pièce *montre*. Celui-ci dit d'où elle vient et comment on la prend — donc
 * quel appareil de la tablette ouvrir, et rien d'autre. Les deux axes se croisent : une vidéo de
 * regard est de famille « Regard ».
 */
export type AttachmentKind = 'photo' | 'document' | 'video'

/**
 * Ce que la fenêtre propose : tout sauf la photo, qui s'ouvre directement depuis sa tuile
 * (GHP, 22/09 — frame Terrain 1.7b).
 */
export type PickedKind = Exclude<AttachmentKind, 'photo'>

/**
 * La fenêtre d'ajout d'un document ou d'une vidéo : le type de fichier, puis le nom — **avant**
 * d'ouvrir le sélecteur ou l'appareil.
 *
 * ⚠️ Dans cet ordre : le nom est ce que le bureau lira sous la pièce, et personne ne le retape
 * après coup une fois la tournée finie. Le demander avant la prise le met sur le chemin.
 */
export interface AddFileModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Le type retenu et son nom — `null` quand l'agent n'en a pas écrit. C'est à l'appelant
   * d'ouvrir l'appareil ou le sélecteur : lui seul sait quel `<input>` porte quel `accept`.
   */
  onConfirm: (kind: PickedKind, caption: string | null) => void
  /** Ce qu'une vidéo peut peser : 1 Go en direct, 25 Mo par le relais (#418). */
  videoLimitBytes?: number
}

const KINDS: readonly { value: PickedKind; label: ReactNode }[] = [
  {
    value: 'document',
    label: (
      <>
        <FileText size={18} aria-hidden /> Document
      </>
    ),
  },
  {
    value: 'video',
    label: (
      <>
        <Video size={18} aria-hidden /> Vidéo
      </>
    ),
  },
]

export function AddFileModal({
  open,
  onOpenChange,
  onConfirm,
  videoLimitBytes = MEDIA_UPLOAD_LIMIT_BYTES,
}: AddFileModalProps) {
  const [kind, setKind] = useState<PickedKind>('document')
  const [caption, setCaption] = useState('')

  // ⚠️ La remise à zéro se fait **à l'ouverture**, jamais à la fermeture : vider en fermant
  // effacerait sous les yeux de l'agent ce qu'il vient d'écrire, le temps de l'animation de
  // sortie — et la fenêtre se ferme précisément au moment où il valide.
  useEffect(() => {
    if (open) {
      setKind('document')
      setCaption('')
    }
  }, [open])

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      icon={<FilePlus2 size={18} />}
      title="Ajouter un fichier"
      footer={
        <>
          <Button
            tone="brand"
            onClick={() => {
              const written = caption.trim()
              onConfirm(kind, written === '' ? null : written)
            }}
          >
            <Check size={18} aria-hidden />
            Ajouter
          </Button>
          <Button
            tone="ghost"
            onClick={() => {
              onOpenChange(false)
            }}
          >
            Annuler
          </Button>
        </>
      }
    >
      <div className={s.form}>
        <SegmentedControl
          label="Type de fichier"
          options={KINDS}
          value={kind}
          onValueChange={setKind}
        />
        <Field
          label="Nom de la pièce"
          hint="Repris dans le back-office. Sans nom, la pièce est signalée à nommer."
        >
          {/* `id` et non `aria-labelledby` : le `<label htmlFor>` de `Field` se rattache à un
              `<input>` natif. */}
          {({ id, hintId }) => (
            <Input
              id={id}
              aria-describedby={hintId}
              value={caption}
              onValueChange={setCaption}
              placeholder="ex. Test au colorant — lave-linge"
            />
          )}
        </Field>
        {/*
         * La note dit le plafond réel, sans quoi l'agent filmerait pour un refus au choix. La
         * frame 1.8b annonçait une compression sur la tablette : écartée le 09/10 (#418), la
         * vidéo part telle que la caméra l'a faite.
         */}
        {kind === 'video' && videoLimitBytes > MEDIA_UPLOAD_LIMIT_BYTES ? (
          <InfoNote icon={<Info size={15} />}>
            Une vidéo de la caméra d’inspection : le serveur accepte{' '}
            <b>{videoLimitBytes / 1024 / 1024 / 1024} Go au plus</b>, sans compression. Elle monte à
            la transmission.
          </InfoNote>
        ) : kind === 'video' ? (
          <InfoNote tone="warn" icon={<Info size={15} />}>
            Un <b>clip court</b> : le serveur accepte {MEDIA_UPLOAD_LIMIT_BYTES / 1024 / 1024} Mo au
            plus, soit environ 12 secondes en 1080p.
          </InfoNote>
        ) : (
          <InfoNote icon={<Info size={15} />}>
            Un document (PDF, plan…) est importé depuis la tablette.
          </InfoNote>
        )}
      </div>
    </Modal>
  )
}
